import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { I18nProvider, I18nScope } from "@/i18n";

// Layouts and Pages
import Sidebar from "@/components/layout/Sidebar";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import PropertiesPage from "@/pages/PropertiesPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import BookingsPage from "@/pages/BookingsPage";
import BookingDetailPage from "@/pages/BookingDetailPage";
import CalendarPage from "@/pages/CalendarPage";
import OwnersPage from "@/pages/OwnersPage";
import PayoutsPage from "@/pages/PayoutsPage";
import UsersPage from "@/pages/UsersPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PropertyPreviewPage from "@/pages/PropertyPreviewPage";
import SettingsPage from "@/pages/SettingsPage";
import ProfilePage from "@/pages/ProfilePage";
import BrowsePropertiesPage from "@/pages/BrowsePropertiesPage";
import TeamPage from "@/pages/TeamPage";
import BillingPage from "@/pages/BillingPage";
import SalesPropertiesPage from "@/pages/SalesPropertiesPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const data = response.data;

      // 2FA required — return flag so LoginPage can handle OTP step
      if (data.requires_2fa) {
        return { success: false, requires_2fa: true, temp_token: data.temp_token };
      }

      const { access_token, user: userData } = data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  // Called after successful OTP verification to sync auth state
  const loginWithToken = (token, userData) => {
    localStorage.setItem('token', token);
    setToken(token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Axios interceptor
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => axios.interceptors.request.eject(interceptor);
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, login, loginWithToken, logout, loading, refreshUser: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#e1dad2] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Main Layout with Sidebar
const MainLayout = ({ children }) => {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar user={user} isCollapsed={sidebarCollapsed} setIsCollapsed={setSidebarCollapsed} />
      <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'} p-6 md:p-8 overflow-auto`}>
        {children}
      </main>
    </div>
  );
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/preview/:id" element={<ProtectedRoute><I18nScope lang="en"><PropertyPreviewPage /></I18nScope></ProtectedRoute>} />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <MainLayout><DashboardPage /></MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/properties" element={
        <ProtectedRoute>
          <MainLayout><PropertiesPage /></MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/properties/:id" element={
        <ProtectedRoute>
          <MainLayout><PropertyDetailPage /></MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/bookings" element={
        <ProtectedRoute>
          <MainLayout><BookingsPage /></MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/bookings/:id" element={
        <ProtectedRoute>
          <MainLayout><BookingDetailPage /></MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/calendar" element={
        <ProtectedRoute>
          <MainLayout><CalendarPage /></MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Admin/staff-only routes — pinned to English per product decision:
          "admin and customer service can stay in english only". The
          I18nScope wrapper overrides the user's locale preference. */}
      <Route path="/owners" element={
        <ProtectedRoute allowedRoles={['ADMIN', 'CUSTOMER_SERVICE']}>
          <I18nScope lang="en"><MainLayout><OwnersPage /></MainLayout></I18nScope>
        </ProtectedRoute>
      } />

      <Route path="/payouts" element={
        <ProtectedRoute allowedRoles={['ADMIN', 'OWNER']}>
          <MainLayout><PayoutsPage /></MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/users" element={
        <ProtectedRoute allowedRoles={['ADMIN']}>
          <I18nScope lang="en"><MainLayout><UsersPage /></MainLayout></I18nScope>
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={['ADMIN']}>
          <I18nScope lang="en"><MainLayout><SettingsPage /></MainLayout></I18nScope>
        </ProtectedRoute>
      } />

      <Route path="/team" element={
        <ProtectedRoute allowedRoles={['ADMIN']}>
          <I18nScope lang="en"><MainLayout><TeamPage /></MainLayout></I18nScope>
        </ProtectedRoute>
      } />

      <Route path="/billing" element={
        <ProtectedRoute allowedRoles={['ADMIN']}>
          <I18nScope lang="en"><MainLayout><BillingPage /></MainLayout></I18nScope>
        </ProtectedRoute>
      } />

      <Route path="/sales" element={
        <ProtectedRoute allowedRoles={['ADMIN']}>
          <I18nScope lang="en"><MainLayout><SalesPropertiesPage /></MainLayout></I18nScope>
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute allowedRoles={['ADMIN', 'CUSTOMER_SERVICE', 'OWNER', 'CUSTOMER']}>
          <MainLayout><ProfilePage /></MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/browse" element={
        <ProtectedRoute allowedRoles={['OWNER', 'CUSTOMER']}>
          <MainLayout><BrowsePropertiesPage /></MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
